import React from "react";
import { Modal, Button, Table } from "react-bootstrap";

/** Read field from camelCase or snake_case API payloads */
function field(obj, camel, snake, fallback = null) {
  if (!obj || typeof obj !== "object") return fallback;
  if (camel in obj && obj[camel] != null && obj[camel] !== "") return obj[camel];
  if (snake && snake in obj && obj[snake] != null && obj[snake] !== "") return obj[snake];
  return fallback;
}

const ViewCollectionRequestModal = ({ show, handleClose, collectionRequest }) => {
  if (!collectionRequest) return null;

  const req = collectionRequest;

  const type = field(req, "type", "type");
  const amount = field(req, "amount", "amount");
  const method = field(req, "method", "method");
  const nameOnCheque = field(req, "nameOnCheque", "name_on_cheque");
  const transactionNumber = field(req, "transactionNumber", "transaction_number");
  const status = field(req, "status", "status");
  const reason = field(req, "reason", "reason");
  const inOut =
    req.in_or_out ??
    req.inOrOut ??
    field(req, "direction", null);

  const {
    created_at,
    updated_at,
    broker,
    cil,
  } = req;

  const brokerExcludedKeys = [
    "type",
    "status",
    "businessCard",
    "swiftCode",
    "branchNumber",
    "bankAddress",
    "picture",
    "address",
    "fcm",
    "developer_id",
    "autoApprove",
    "isBlocked",
    "created_at",
    "updated_at"
  ];

  const friendlyLabels = {
    type: "Collection Type",
    amount: "Amount",
    method: "Method",
    nameOnCheque: "Name On Cheque",
    transactionNumber: "Bank / Transaction Reference",
    status: "Status",
    reason: "Admin / Decline Reason",
    inOut: "Direction",
    created_at: "Created At",
    updated_at: "Updated At",
    name: "Name",
    email: "Email",
    mobile: "Mobile Number",
    mobileOfReference: "Reference Mobile",
    iban: "IBAN",
    nameOfAccount: "Account Name",
    bankName: "Bank Name",
    clientName: "Client Name",
    clientNumber: "Client Number",
    projectName: "Project Name",
    developerName: "Developer Name",
    developerSalesNumber: "Developer Sales Number",
    unitCode: "Unit Code",
    typeOfUnit: "Type of Unit",
    dealValue: "Deal Value",
    actualAmount: "Actual Amount",
    document: "Document",
    expirationDate: "Expiration Date",
  };

  const methodLower = String(method || type || "").toLowerCase();

  const renderTableRows = (dataObj, excludedKeys = []) => {
    if (!dataObj || typeof dataObj !== "object") return null;
    return Object.entries(dataObj).map(([key, value]) => {
      if (key.toLowerCase().includes("id")) return null;
      if (excludedKeys.includes(key)) return null;

      let displayVal = value;
      if (key === "created_at" || key === "updated_at") {
        displayVal = value ? new Date(value).toLocaleString() : "-";
      }

      if (typeof displayVal === "string" && displayVal.startsWith("http")) {
        return (
          <tr key={key}>
            <td style={{ width: "30%", fontWeight: "bold" }}>
              {friendlyLabels[key] || key}
            </td>
            <td>
              <img src={displayVal} alt={key} style={{ maxWidth: "150px" }} />
            </td>
          </tr>
        );
      }

      return (
        <tr key={key}>
          <td style={{ width: "30%", fontWeight: "bold" }}>
            {friendlyLabels[key] || key}
          </td>
          <td>{displayVal !== null && displayVal !== undefined ? String(displayVal) : "-"}</td>
        </tr>
      );
    });
  };

  /** Highlight row for cheque / bank payouts */
  const summaryRows = [];

  const pushSummary = (label, value, keySuffix) => {
    if (value === null || value === undefined || value === "") return;
    summaryRows.push(
      <tr key={label + keySuffix}>
        <td style={{ width: "35%", fontWeight: "bold" }}>{label}</td>
        <td>{String(value)}</td>
      </tr>
    );
  };

  pushSummary("Request type / reason", type, "t");
  if (amount !== null && amount !== undefined && amount !== "") {
    const n = Number(amount);
    const amt = Number.isFinite(n)
      ? n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : String(amount);
    pushSummary("Amount", amt, "a");
  }
  pushSummary("Withdrawal method", method, "m");

  if (methodLower.includes("cheque")) {
    pushSummary("Cheque — name on cheque", nameOnCheque ?? "—", "c");
  } else if (methodLower.includes("bank") || type?.toLowerCase?.().includes("bank")) {
    pushSummary("Bank transaction reference", transactionNumber ?? "—", "b");
    pushSummary("Beneficiary / name on account", nameOnCheque ?? "—", "n");
  } else {
    pushSummary("Name on cheque / payee", nameOnCheque, "noc");
    pushSummary("Bank / transaction reference", transactionNumber, "txn");
  }

  pushSummary("Status", status, "st");
  if (reason) pushSummary("Notes / rejection reason", reason, "rs");

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Collection Request Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {summaryRows.length > 0 && (
          <>
            <h6 className="mb-12 text-muted">Payment summary</h6>
            <Table striped bordered hover responsive size="sm" className="mb-4">
              <tbody>{summaryRows}</tbody>
            </Table>
          </>
        )}

        <h5 className="mb-3">All fields</h5>
        <Table striped bordered hover responsive>
          <tbody>
            {renderTableRows({
              type,
              amount,
              method,
              nameOnCheque,
              transactionNumber,
              status,
              reason,
              inOut,
              created_at,
              updated_at,
            })}
          </tbody>
        </Table>

        {broker && (
          <>
            <h5 className="mt-4 mb-3">Broker Details</h5>
            <Table striped bordered hover responsive>
              <tbody>{renderTableRows(broker, brokerExcludedKeys)}</tbody>
            </Table>
          </>
        )}

        {cil && (
          <>
            <h5 className="mt-4 mb-3">CIL Details</h5>
            <Table striped bordered hover responsive>
              <tbody>{renderTableRows(cil)}</tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewCollectionRequestModal;
